import { useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { getCookie, setCookie } from './inboundHelper';

import { getEventDetails } from '@store/slices/eventSlice';
import { generatePassword, safeJSONParse } from './../fn';

export const useInbound = () => {
  const dispatch = useDispatch();
  const sluggedEvent = useSelector(getEventDetails)?.[0];
  const [inboundCookie, setInboundCookie] = useState<string | undefined>();

  useEffect(() => {
    const inbCookie = getCookie(`event-${sluggedEvent?.slug}-inbound`);
    setInboundCookie(inbCookie || undefined);
  }, [sluggedEvent?.slug]);

  const shouldShowForm = useMemo(() => {
    return !inboundCookie;
  }, [inboundCookie]);

  const updateInboundCookie = useCallback(
    (data?: Record<string, string>) => {
      if (!data?.event || !data) return;

      const cookieData = {
        ...data,
      };

      const cookieName = `event-${sluggedEvent?.slug}-inbound`;
      const cookieValue = JSON.stringify(cookieData);

      setInboundCookie(cookieValue);
      setCookie(cookieName, cookieValue, 30);
    },
    [dispatch]
  );

  const registerInbound = useCallback(
    async (data: Record<string, string>, callback?: () => void) => {
      const formData = new URLSearchParams();
      formData.append('event', data.event);
      formData.append('path', data.path);
      formData.append('action', data.action);
      // ------------------------------------
      formData.append('address_country', data.address_country.toUpperCase());
      formData.append('company_name', data.company_name);
      formData.append('contact_mobile', data.contact_mobile);
      formData.append('email', data.email);
      formData.append('first_name', data.first_name);
      formData.append('job_title', data.job_title);
      formData.append('last_name', data.last_name);
      formData.append('salutation', data.salutation);

      const password = await generatePassword(data.email);

      await fetch(`/api/inbound/register`, {
        method: 'POST',
        headers: {
          'Authorization': password,
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: formData.toString(),
      })
        .then(async response => await response.json())
        .then(data => {
          const newInboundData = {
            ...(data?.data ?? {}),
            ...(data?.data?.client ?? {}),
          };
          delete newInboundData.client;
          updateInboundCookie(newInboundData);

          if (callback) {
            callback();
          }
        })
        .catch(e => {
          console.log('registerInbound error = ', e);
        });
    },
    [dispatch, inboundCookie]
  );

  const actionInbound = useCallback(
    async (data: Record<string, any>, callback?: () => void) => {
      const formData = new URLSearchParams();
      formData.append('event', data.event);
      formData.append('path', data.path);
      formData.append('action', data.action);
      // ------------------------------------
      formData.append(
        'address_country',
        data?.address_country.toUpperCase() ?? ''
      );
      formData.append('company_name', data?.company_name ?? '');
      formData.append('contact_mobile', data?.contact_mobile ?? '');
      formData.append('email', data?.email ?? '');
      formData.append('first_name', data?.first_name ?? '');
      formData.append('job_title', data?.job_title ?? '');
      formData.append('last_name', data?.last_name ?? '');
      formData.append('salutation', data?.salutation ?? '');

      const password = await generatePassword(data?.email ?? '');

      fetch(`/api/inbound/action`, {
        method: 'POST',
        headers: {
          Authorization: password,
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: formData.toString(),
      })
        .then(async response => await response.json())
        .then(data => {
          const newInboundData = {
            ...(data?.data ?? {}),
            ...(data?.data?.client ?? {}),
          };
          delete newInboundData.client;
          updateInboundCookie(newInboundData);

          if (callback) {
            callback();
          }
        })
        .catch(e => {
          console.log('actionInbound error = ', e);
        });
    },
    [dispatch, inboundCookie]
  );

  return {
    inboundCookie,
    updateInboundCookie,
    actionInbound,
    registerInbound,
    email: safeJSONParse(inboundCookie)?.email,
    shouldShowForm,
  };
};
